import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  Instagram,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  // eslint-disable-next-line
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const serviceNames = {
      'logotipo': 'Criação de Logotipo',
      'identidade': 'Identidade Visual',
      'branding': 'Projeto de Branding',
      'papelaria': 'Papelaria Corporativa',
      'digital': 'Design Digital',
      'outros': 'Outros Projetos'
    };

    const whatsappMessage = `Olá! Vim através do site e gostaria de solicitar um orçamento.

*Dados do Cliente:*
• Nome: ${formData.name}
• E-mail: ${formData.email}
• Telefone: ${formData.phone || 'Não informado'}

*Serviço Desejado:*
${serviceNames[formData.subject] || formData.subject}

*Mensagem:*
${formData.message}

Aguardo retorno. Obrigado(a)!`;

    const whatsappNumber = '5515981168798';
    
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappURL, '_blank');

    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    
    setSubmitStatus('success');
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={18} />,
      title: "E-mail",
      info: "jmbdesigner@gmail.com",
      link: "mailto:jmbdesigner@gmail.com"
    },
    {
      icon: <Phone size={18} />,
      title: "Telefone",
      info: "+55 (15) 98116-8798",
      link: "tel:+5515981168798"
    },
    {
      icon: <MapPin size={18} />,
      title: "Localização",
      info: "Sorocaba, SP",
      link: null
    }
  ];

  const socialLinks = [
    {
      icon: <Linkedin size={18} />,
      name: "LinkedIn",
      url: "https://www.linkedin.com/in/jo-marcos-batista/",
      color: "#0077b5"
    },
    {
      icon: <Instagram size={18} />,
      name: "Instagram",
      url: "https://www.instagram.com/jmb.designer/",
      color: "#e4405f"
    }
  ];

  return (
    <section id="contact" className="contact">
      <div className="container">
        <motion.div
          className="contact-header"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            VAMOS CONVERSAR?
          </motion.h2>
          <motion.p className="section-subtitle" variants={itemVariants}>
            Transforme suas ideias em realidade. Entre em contato para discutirmos 
            seu próximo projeto e descobrir como posso ajudar a elevar sua marca.
          </motion.p>
        </motion.div>

        <motion.div
          className="contact-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <div className="contact-info">
            <motion.h3 variants={itemVariants}>Entre em Contato</motion.h3>
            
            <motion.div className="info-cards" variants={itemVariants}>
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  className="info-card"
                >
                  <div className="info-icon">
                    {item.icon}
                  </div>
                  <div className="info-content">
                    <span className="info-title">{item.title}</span>
                    {item.link ? (
                      <a href={item.link} className="info-link">
                        {item.info}
                      </a>
                    ) : (
                      <span className="info-text">{item.info}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="social-links" variants={itemVariants}>
              <h4>Redes Sociais</h4>
              <div className="social-buttons">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    className="social-button"
                    style={{ '--social-color': social.color }}
                    whileTap={{ scale: 0.97 }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.icon}
                    <span>{social.name}</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div className="contact-form" variants={itemVariants}>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nome Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Seu nome completo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">E-mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Telefone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Assunto *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione um assunto</option>
                    <option value="logotipo">Criação de Logotipo</option>
                    <option value="identidade">Identidade Visual</option>
                    <option value="branding">Projeto de Branding</option>
                    <option value="papelaria">Papelaria Corporativa</option>
                    <option value="digital">Design Digital</option>
                    <option value="outros">Outros Projetos</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Mensagem *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Conte-me mais sobre seu projeto..."
                ></textarea>
              </div>

              <motion.button
                type="submit"
                className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
                whileTap={{ scale: 0.99 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar via WhatsApp
                    <svg viewBox="0 0 48 48" y="0px" x="0px" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98c-0.001,0,0,0,0,0h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303z" fill="#fff"></path>
                      <path d="M4.868,43.803c-0.132,0-0.26-0.052-0.355-0.148c-0.125-0.127-0.174-0.312-0.127-0.483l2.639-9.636c-1.636-2.906-2.499-6.206-2.497-9.556C4.532,13.238,13.273,4.5,24.014,4.5c5.21,0.002,10.105,2.031,13.784,5.713c3.679,3.683,5.704,8.577,5.702,13.781c-0.004,10.741-8.746,19.48-19.486,19.48c-3.189-0.001-6.344-0.788-9.144-2.277l-9.875,2.589C4.953,43.798,4.911,43.803,4.868,43.803z" fill="#fff"></path>
                      <path d="M24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,4C24.014,4,24.014,4,24.014,4C12.998,4,4.032,12.962,4.027,23.979c-0.001,3.367,0.849,6.685,2.461,9.622l-2.585,9.439c-0.094,0.345,0.002,0.713,0.254,0.967c0.19,0.192,0.447,0.297,0.711,0.297c0.085,0,0.17-0.011,0.254-0.033l9.687-2.54c2.828,1.468,5.998,2.243,9.197,2.244c11.024,0,19.99-8.963,19.995-19.98c0.002-5.339-2.075-10.359-5.848-14.135C34.378,6.083,29.357,4.002,24.014,4L24.014,4z" fill="#cfd8dc"></path>
                      <path d="M35.176,12.832c-2.98-2.982-6.941-4.625-11.157-4.626c-8.704,0-15.783,7.076-15.787,15.774c-0.001,2.981,0.833,5.883,2.413,8.396l0.376,0.597l-1.595,5.821l5.973-1.566l0.577,0.342c2.422,1.438,5.2,2.198,8.032,2.199h0.006c8.698,0,15.777-7.077,15.78-15.776C39.795,19.778,38.156,15.814,35.176,12.832z" fill="#40c351"></path>
                      <path clipRule="evenodd" d="M19.268,16.045c-0.355-0.79-0.729-0.806-1.068-0.82c-0.277-0.012-0.593-0.011-0.909-0.011c-0.316,0-0.83,0.119-1.265,0.594c-0.435,0.475-1.661,1.622-1.661,3.956c0,2.334,1.7,4.59,1.937,4.906c0.237,0.316,3.282,5.259,8.104,7.161c4.007,1.58,4.823,1.266,5.693,1.187c0.87-0.079,2.807-1.147,3.202-2.255c0.395-1.108,0.395-2.057,0.277-2.255c-0.119-0.198-0.435-0.316-0.909-0.554s-2.807-1.385-3.242-1.543c-0.435-0.158-0.751-0.237-1.068,0.238c-0.316,0.474-1.225,1.543-1.502,1.859c-0.277,0.317-0.554,0.357-1.028,0.119c-0.474-0.238-2.002-0.738-3.815-2.354c-1.41-1.257-2.362-2.81-2.639-3.285c-0.277-0.474-0.03-0.731,0.208-0.968c0.213-0.213,0.474-0.554,0.712-0.831c0.237-0.277,0.316-0.475,0.474-0.791c0.158-0.317,0.079-0.594-0.04-0.831C20.612,19.329,19.69,16.983,19.268,16.045z" fillRule="evenodd" fill="#fff"></path>
                    </svg>
                  </>
                )}
              </motion.button>
            </form>

            {submitStatus && (
              <motion.div
                role="status"
                className={`submit-status ${submitStatus}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {submitStatus === 'success' ? (
                  <>
                    <CheckCircle size={20} />
                    <span>Mensagem enviada com sucesso! Retornarei em breve.</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={20} />
                    <span>Erro ao enviar mensagem. Tente novamente.</span>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
